import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AuthCallback - Processes the session_id from URL fragment after Google OAuth.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');

      if (!sessionId) {
        window.location.replace('/');
        return;
      }

      try {
        const res = await axios.post(`${API}/auth/session`, { session_id: sessionId }, {
          withCredentials: true,
          timeout: 15000,
        });
        // Store token in localStorage as fallback for cookie issues
        const token = res.data?.session_token;
        if (token) {
          localStorage.setItem('kaha_session', token);
        }
        // Redirect to stored return path (e.g. /admin) or home
        const returnPath = localStorage.getItem('kaha_auth_return') || '/';
        localStorage.removeItem('kaha_auth_return');
        // Clear hash before redirect to prevent re-triggering
        window.history.replaceState(null, '', window.location.pathname);
        window.location.replace(returnPath);
      } catch (err) {
        console.warn('Auth callback error:', err.message);
        setError(err.response?.data?.detail || err.message || 'Error al verificar sesión');
        localStorage.removeItem('kaha_auth_return');
        // Auto-redirect after showing error
        setTimeout(() => {
          window.history.replaceState(null, '', '/');
          window.location.replace('/admin-login');
        }, 3000);
      }
    };

    processAuth();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-sm p-6">
          <p className="text-red-600 font-medium mb-2">Error de autenticación</p>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <p className="text-xs text-muted-foreground">Redirigiendo al inicio de sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Verificando acceso...</p>
      </div>
    </div>
  );
};

/**
 * AdminProtectedRoute - Wraps admin pages. Checks auth + is_admin via /api/auth/me.
 */
export const AdminProtectedRoute = ({ children }) => {
  const [authState, setAuthState] = useState(null); // null=checking, true=ok, false=denied
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('kaha_session');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true, headers });
        if (!response.data.is_admin) {
          setAuthState(false);
          navigate('/', { replace: true });
          return;
        }
        setUser(response.data);
        setAuthState(true);
      } catch {
        setAuthState(false);
        navigate('/admin-login', { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  if (authState === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (authState === false) return null;

  return React.cloneElement(children, { adminUser: user });
};

export default AdminProtectedRoute;
